import { 
  Bot, 
  Sparkles, 
  ShieldCheck, 
  CheckCircle2, 
  ChevronRight, 
  Layers, 
  Search, 
  ShoppingCart, 
  FileSpreadsheet, 
  Zap,
  Lock
} from 'lucide-react';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { cn } from '../../../lib/utils.js';
import { 
  ALWIS_CORE_CAPABILITIES, 
  SECURITY_COMPLIANCE 
} from '../executive-data.js';

export const ALWIS_FEATURE_CARDS = [
  {
    id: 'bma',
    code: 'DIN 14675 / EN 54',
    title: 'Brandmeldeanlagen (BMA)',
    subtitle: 'Loop-Kalkulation & 72h Akkukapazität',
    description: 'Findet kompatible Komponenten, Pulsar Netzteile und Melder für Brandmeldesysteme (z. B. SATEL ACSP).',
    icon: Layers,
    color: 'text-amber-600 bg-amber-50 border-amber-200',
  },
  {
    id: 'ema',
    code: 'VdS 2311 / EN 50131',
    title: 'Einbruchmeldeanlagen (EMA)',
    subtitle: 'Grade 2 & 3 / Anti-Masking',
    description: 'Findet passende Erweiterungen, Bewegungsmelder, Tastaturen und Notstromversorgung für Alarmzentralen.',
    icon: Search,
    color: 'text-blue-600 bg-blue-50 border-blue-200',
  },
  {
    id: 'bom',
    code: '1-Klick-Planer',
    title: 'Stücklisten-BoM-Import',
    subtitle: 'Direkter Warenkorb-Transfer',
    description: 'Generiert vollständige B2B-Stücklisten mit Montagezubehör und überträgt diese per 1-Klick in den Shop-Warenkorb.',
    icon: FileSpreadsheet,
    color: 'text-emerald-600 bg-emerald-50 border-emerald-200',
  },
];

export function AlwisPillarCard({
  capabilities = ALWIS_CORE_CAPABILITIES,
  norms = capabilities,
  zeroAudioRetention = SECURITY_COMPLIANCE.zeroAudioRetention,
  onOpenCopilot,
  onOpenTab,
  isDetailed = false,
  className,
}) {
  const handleCopilotClick = (prompt) => {
    if (onOpenCopilot) {
      onOpenCopilot(prompt || 'Welches Netzteil und Zubehör passt zu einer SATEL Zentrale?');
    }
  };

  const handleOpenPillar = () => {
    if (onOpenTab) {
      onOpenTab('alwis');
    }
  };

  return (
    <div
      data-testid="pillar-card-alwis"
      className={cn(
        'flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-6 shadow-xs transition-all hover:border-[var(--primary,#e3000b)] hover:shadow-sm',
        className
      )}
    >
      <div className="space-y-4">
        {/* Header Badge Strip */}
        <div className="flex items-center justify-between">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-[var(--primary,#e3000b)] border border-red-200">
            <Bot className="h-5 w-5" />
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-red-50 text-[var(--primary,#e3000b)] border-red-200 text-xs font-bold">
              Säule 2
            </Badge>
            <Badge variant="outline" className="bg-slate-100 text-slate-800 border-slate-300 text-[11px] font-semibold">
              KI Copilot
            </Badge>
          </div>
        </div>

        {/* Title & Scope Description */}
        <div>
          <h3 className="font-display text-base font-bold text-slate-900 leading-snug">
            ALWIS KI-Fachberater
          </h3>
          <p className="mt-1 text-xs text-slate-600 leading-relaxed">
            Technischer Assistent für <strong className="font-semibold text-slate-900">Produktauswahl, Zubehör-Kompatibilität</strong> (DIN 14675 / VdS 2311) und 1-Klick-Stücklisten aus dem ASL-Katalog.
          </p>
        </div>

        {/* Feature Explanation Cards Grid */}
        <div className="space-y-2">
          {ALWIS_FEATURE_CARDS.map((feat) => {
            const Icon = feat.icon;
            return (
              <div
                key={feat.id}
                data-testid={`alwis-norm-${feat.id}`}
                className="group flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3 text-xs transition-colors hover:bg-white hover:border-slate-300"
              >
                <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border', feat.color)}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="flex items-center justify-between">
                    <h4 className="font-display text-xs font-bold text-slate-900 truncate">
                      {feat.title}
                    </h4>
                    <span className="font-mono tabular-nums text-[10px] font-bold text-[var(--primary,#e3000b)] bg-red-50 px-1.5 py-0.2 rounded border border-red-100">
                      {feat.code}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-600 leading-snug">
                    {feat.subtitle} — {feat.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Security & Audio Privacy Status Box (Demo / Unverifiziert) */}
        <div 
          data-testid="alwis-security-status"
          className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs"
        >
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-slate-500 shrink-0" />
            <span className="text-slate-700 font-medium leading-tight">
              Zero Audio Retention: <span className="text-slate-600 font-normal">Architektur-Konzept (Demo / Nicht im Shop-Release verifiziert)</span>
            </span>
          </div>
          <Badge variant="outline" className="bg-white text-slate-700 border-slate-300 text-[10px] font-mono shrink-0 ml-2">
            Demo
          </Badge>
        </div>
      </div>

      {/* Action Buttons Footer */}
      <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="alwis-action-details"
          onClick={handleOpenPillar}
          className="text-xs font-bold gap-1 text-[var(--primary,#e3000b)] hover:bg-[var(--primary,#e3000b)]/5 cursor-pointer focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
        >
          <span>Funktionen</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>

        <Button
          type="button"
          variant="default"
          size="sm"
          data-testid="alwis-copilot-btn"
          onClick={() => handleCopilotClick('Welches Netzteil und Zubehör passt zu einer SATEL Zentrale?')}
          className="text-xs font-bold gap-1.5 bg-[var(--primary,#e3000b)] text-white hover:bg-[var(--primary,#e3000b)]/90 cursor-pointer shadow-xs focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
        >
          <Sparkles className="h-3.5 w-3.5" />
          <span>Copilot starten</span>
        </Button>
      </div>
    </div>
  );
}
